import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForm } from "react-hook-form";
import ky from "ky";
import { useNavigate } from "react-router-dom";
import { config } from "../services/config";
import toast from "react-hot-toast";

const TEMPLATE_URL =
  "https://pineapple-gondola-f92.notion.site/AutoDocs-Template-a656e54002e844279feda22ff661e992";

const schema = z
  .object({
    notionUrl: z.string().url(),
  })
  .required();
type FormData = z.infer<typeof schema>;

// Store available pages locally
export function Home() {
  const navigate = useNavigate();

  const { register, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    try {
      const res = await ky
        .get(`${config.REACT_APP_BACKEND_URL}/generate?url=${data.notionUrl}`)
        .text();
      navigate(`/site/edit/${res}`);
    } catch (e: any) {
      const res = await e.response.json();
      console.error(res.error);
      // TODO: Handle different kinds of errors
      throw new Error(res.error);
    }
  };

  // TODO: Handle react-hook-form errors

  return (
    <div className="bg-gray-50">
      <div className="relative overflow-hidden">
        <div className="absolute inset-y-0 h-full w-full" aria-hidden="true">
          <div className="relative h-full">
            <svg
              className="absolute right-full translate-y-1/3 translate-x-1/4 transform sm:translate-x-1/2 md:translate-y-1/2 lg:translate-x-full"
              width={404}
              height={784}
              fill="none"
              viewBox="0 0 404 784"
            >
              <defs>
                <pattern
                  id="e229dbec-10e9-49ee-8ec3-0286ca089edf"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width={404}
                height={784}
                fill="url(#e229dbec-10e9-49ee-8ec3-0286ca089edf)"
              />
            </svg>
            <svg
              className="absolute left-full -translate-y-3/4 -translate-x-1/4 transform sm:-translate-x-1/2 md:-translate-y-1/2 lg:-translate-x-3/4"
              width={404}
              height={784}
              fill="none"
              viewBox="0 0 404 784"
            >
              <defs>
                <pattern
                  id="d2a68204-c383-44b1-b99f-42ccff4e5365"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width={404}
                height={784}
                fill="url(#d2a68204-c383-44b1-b99f-42ccff4e5365)"
              />
            </svg>
          </div>
        </div>
        <div className="mx-auto mt-16 max-w-7xl px-4 sm:mt-24 sm:px-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
              <span className="block">Instant docs</span>
              <span className="block text-emerald-600">
                anyone on your team can edit
              </span>
            </h1>
            <p className="mx-auto mt-3 max-w-md pb-4 text-base text-gray-500 sm:text-lg md:mt-5 md:max-w-3xl md:text-xl">
              Use Notion as a CMS for your docs. No code required.
            </p>
          </div>
        </div>
        <div className="bg-white w-full h-screen flex flex-col">
          <div className="gap-y-4 z-10 bg-white flex flex-col w-[800px] mx-auto justify-center mt-16">
            <nav aria-label="Progress">
              <ol className="space-y-4 md:flex md:space-y-0 md:space-x-8">
                <li className="md:w-1/2">
                  <div
                    className="group flex flex-col border-l-4 border-emerald-600 py-2 pl-4
               hover:border-emerald-800 md:border-l-0 md:border-t-4 md:pl-0 md:pt-4 md:pb-0"
                  >
                    <span
                      className="text-sm font-medium text-emerald-600 
                group-hover:text-emerald-800"
                    >
                      Step 1
                    </span>
                    <span className="text-sm font-medium mb-4">
                      Duplicate this Notion template and make it public.
                    </span>
                    <a
                      href={TEMPLATE_URL}
                      className="flex justify-around"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <button
                        type="button"
                        className="inline-flex items-center px-6 py-3 border border-transparent
                   text-base font-medium rounded-full shadow-sm text-white
                    bg-emerald-600 hover:bg-emerald-700 focus:outline-none 
                    focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                      >
                        Duplicate this template
                      </button>
                    </a>
                    <div className="text-xs mt-4">
                      You can technically use any Notion page. We'll crawl
                      through its sub pages to build out the page hierarchy.
                    </div>
                  </div>
                </li>
                <li className="md:w-1/2">
                  <div
                    className="group flex flex-col border-l-4 border-emerald-600 py-2 pl-4 
              hover:border-emerald-800 md:border-l-0 md:border-t-4 md:pl-0 md:pt-4 md:pb-0"
                  >
                    <span
                      className="text-sm font-medium text-emerald-600 
                group-hover:text-emerald-800"
                    >
                      Step 2
                    </span>
                    <span className="text-sm font-medium">
                      Paste your public Notion URL or try it with the template
                    </span>
                    <form
                      onSubmit={handleSubmit((data) =>
                        toast.promise(onSubmit(data), {
                          loading: "Generating your website...",
                          success: <b>Tada!</b>,
                          error: (err) => (
                            <b>
                              We ran into some issues: <br /> {err.toString()}
                            </b>
                          ),
                        })
                      )}
                      className="flex flex-col items-end gap-y-4 mb-8"
                    >
                      <input
                        {...register("notionUrl")}
                        type="text"
                        className="text-black w-full mt-4 text-xs p-2 rounded"
                        defaultValue={TEMPLATE_URL}
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                      <input
                        type="submit"
                        value="Generate"
                        className="items-center px-6 py-3 border border-transparent
                        text-base font-medium rounded-full shadow-sm text-white
                         bg-emerald-600 hover:bg-emerald-700 focus:outline-none 
                         focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 cursor-pointer"
                      />
                    </form>
                  </div>
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
